/**
 * GazeEye — The eye of IARisk that watches you back.
 *
 * The whole element is the eye image. The pupil dot tracks the cursor using
 * a CSS custom property (--gaze-x / --gaze-y). On hover, red lightning glows
 * brighter. A subtle heartbeat pulse keeps the eye "alive" when idle.
 *
 * Layered on top:
 *   - 6 red lightning SVG arcs (random flicker)
 *   - 4 cyan datastream lines (horizontal scan)
 *   - 2 smoke wisps (left/right drift)
 *   - 1 glitch ghost (RGB-split version of the pupil)
 *
 * Respects prefers-reduced-motion (kills all animation, keeps cursor track).
 */
import React, { useEffect, useRef, useState } from "react";

const EYE_IMG =
  "https://customer-assets.emergentagent.com/job_41e16eaf-ebb8-4261-8508-81a739cb23ea/artifacts/l5kke7cg_image.png";

export default function GazeEye({ size = 460, className = "" }) {
  const wrapRef = useRef(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let raf = 0;
    const max = 12; // px pupil travel
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = (e.clientX - cx) / window.innerWidth;
      const dy = (e.clientY - cy) / window.innerHeight;
      const x = Math.max(-1, Math.min(1, dx * 2)) * max;
      const y = Math.max(-1, Math.min(1, dy * 2)) * max;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--gaze-x", `${x}px`);
        el.style.setProperty("--gaze-y", `${y}px`);
      });
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Lightning bolts SVG (decorative) — short edge-only bolts to keep
  // the centre of the iris readable. Five strokes positioned around the
  // border so they read like "fissures" rather than a tangled web.
  const bolts = [
    { d: "M 50 5 L 48 18 L 56 16 L 52 32", delay: "0s" },
    { d: "M 92 45 L 78 47 L 80 53 L 66 50", delay: "0.6s" },
    { d: "M 50 95 L 52 82 L 44 84 L 48 68", delay: "1.2s" },
    { d: "M 8 55 L 22 53 L 20 47 L 34 50", delay: "1.8s" },
    { d: "M 78 18 L 70 28 L 76 30 L 66 38", delay: "2.4s" },
    { d: "M 22 80 L 30 70 L 24 68 L 34 60", delay: "3.0s" },
  ];

  return (
    <div
      ref={wrapRef}
      className={`relative inline-block eye-vignette ${className}`}
      style={{
        width: size,
        height: size,
        "--gaze-x": "0px",
        "--gaze-y": "0px",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="gaze-eye"
    >
      {/* Outer red glow halo — soft, no hard ring boundary */}
      <div
        className={`absolute -inset-12 transition-opacity duration-700 pointer-events-none ${
          hover ? "opacity-100" : "opacity-80"
        }`}
        style={{
          background:
            "radial-gradient(circle at center, rgba(255, 26, 58, 0.55) 0%, rgba(255, 26, 58, 0.30) 28%, rgba(255, 26, 58, 0.08) 55%, transparent 75%)",
          filter: "blur(10px)",
        }}
        aria-hidden="true"
      />

      {/* Pink neon "loading / generating" halo — pulses continuously.
          Requested by the user: contorno rosa palpitante como si estuviera
          cargando/generando. Sits BEHIND the eye + on top of the red halo. */}
      <div
        className="absolute -inset-6 rounded-full pointer-events-none pink-pulse-halo"
        style={{
          background:
            "radial-gradient(circle at center, rgba(255, 20, 147, 0.55) 0%, rgba(255, 60, 180, 0.30) 30%, rgba(255, 105, 220, 0.10) 55%, transparent 75%)",
        }}
        aria-hidden="true"
        data-testid="eye-pink-halo"
      />

      {/* Pink neon ring — matches the pink contour on the eye image and
          breathes in sync with the halo. */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none pink-pulse-ring"
        aria-hidden="true"
        data-testid="eye-pink-ring"
      />

      {/* Smoke wisps */}
      <div
        className="absolute -left-16 top-1/2 -translate-y-1/2 w-32 h-48 rounded-full smoke-drift-l pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(180,180,200,0.35) 0%, transparent 65%)",
          filter: "blur(20px)",
        }}
        aria-hidden="true"
      />
      <div
        className="absolute -right-16 top-1/2 -translate-y-1/2 w-32 h-48 rounded-full smoke-drift-r pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(180,180,200,0.35) 0%, transparent 65%)",
          filter: "blur(20px)",
        }}
        aria-hidden="true"
      />

      {/* The eye image itself, clipped to a circle and softly masked
          so the square edges fade seamlessly into the background. */}
      <div
        className="relative w-full h-full rounded-full overflow-hidden"
        style={{
          // Outer red glow via box-shadow (instead of inset which creates a
          // visible ring inside the circle). drop-shadow on parent handles the
          // bigger atmospheric halo.
          boxShadow: "0 0 80px rgba(255, 26, 58, 0.5)",
        }}
      >
        {/* GHOST/PHANTOM eye — duplicated, blurred, breathing.
            Sits BEHIND the main eye to create an aura silhouette. */}
        <img
          src={EYE_IMG}
          alt=""
          aria-hidden="true"
          draggable="false"
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          style={{
            transform:
              "translate3d(calc(var(--gaze-x) * -0.7), calc(var(--gaze-y) * -0.7), 0) scale(1.32)",
            transition: "transform 600ms cubic-bezier(0.16, 1, 0.3, 1)",
            filter:
              "blur(14px) brightness(1.6) saturate(1.8) hue-rotate(-6deg) contrast(1.1)",
            opacity: 0.55,
            mixBlendMode: "screen",
            WebkitMaskImage:
              "radial-gradient(circle at center, black 25%, rgba(0,0,0,0.5) 50%, transparent 88%)",
            maskImage:
              "radial-gradient(circle at center, black 25%, rgba(0,0,0,0.5) 50%, transparent 88%)",
            animation: "pupil-pulse 5s ease-in-out infinite",
          }}
        />

        {/* GHOST trail #2 — secondary phantom, more diffuse, offset opposite */}
        <img
          src={EYE_IMG}
          alt=""
          aria-hidden="true"
          draggable="false"
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          style={{
            transform:
              "translate3d(calc(var(--gaze-x) * 0.9), calc(var(--gaze-y) * 0.9), 0) scale(1.28)",
            transition: "transform 900ms cubic-bezier(0.22, 1, 0.36, 1)",
            filter: "blur(28px) brightness(1.2) saturate(1.6)",
            opacity: 0.35,
            mixBlendMode: "screen",
            WebkitMaskImage:
              "radial-gradient(circle at center, black 30%, transparent 80%)",
            maskImage:
              "radial-gradient(circle at center, black 30%, transparent 80%)",
          }}
        />

        {/* MAIN eye image — masked at the edges so the square frame
            blends into the dark background instead of clipping abruptly.
            Slight zoom (1.18) so the iris fills the circle and the
            square cyan code frame of the source image stays outside view. */}
        <img
          src={EYE_IMG}
          alt="IARisk — el ojo"
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          draggable="false"
          style={{
            transform:
              "translate3d(calc(var(--gaze-x) * 0.4), calc(var(--gaze-y) * 0.4), 0) scale(1.22)",
            transition: "transform 240ms cubic-bezier(0.16, 1, 0.3, 1)",
            // Boost realism: more saturation, contrast, gentle warmth
            filter:
              "brightness(1.30) contrast(1.22) saturate(1.55) drop-shadow(0 0 20px rgba(255, 26, 58, 0.45))",
            // Smooth circular feather so the eye seems to FLOAT in dark.
            // Aggressive fade kills any residual square framing.
            WebkitMaskImage:
              "radial-gradient(circle at center, black 0%, black 38%, rgba(0,0,0,0.85) 56%, rgba(0,0,0,0.3) 78%, transparent 96%)",
            maskImage:
              "radial-gradient(circle at center, black 0%, black 38%, rgba(0,0,0,0.85) 56%, rgba(0,0,0,0.3) 78%, transparent 96%)",
          }}
        />

        {/* Red ambient wash to amplify the iris glow */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(255,26,58,0.36) 0%, rgba(255,26,58,0.14) 35%, transparent 65%)",
          }}
          aria-hidden="true"
        />

        {/* Cyan iris specular — subtle inner reflection */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen"
          style={{
            background:
              "radial-gradient(circle at 38% 38%, rgba(255,255,255,0.15) 0%, rgba(0,224,255,0.06) 15%, transparent 30%)",
          }}
          aria-hidden="true"
        />

        {/* Lightning bolts overlay */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
        >
          <defs>
            <filter id="lightning-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="1.4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          {bolts.map((b, i) => (
            // Stable key: `bolts` is a module-level constant (never mutated),
            // so pairing the index with the path signature keeps identity
            // across re-renders and satisfies react/no-array-index-key.
            <path
              key={`bolt-${i}-${b.delay}`}
              d={b.d}
              stroke="#ff3050"
              strokeWidth="1.1"
              fill="none"
              strokeLinecap="round"
              opacity={hover ? 1 : 0.92}
              filter="url(#lightning-glow)"
              style={{
                animation: `lightning-pulse 5.2s infinite`,
                animationDelay: b.delay,
              }}
            />
          ))}
        </svg>

        {/* Cyan datastream horizontal beams (3 random heights) */}
        {[28, 50, 72].map((top, i) => (
          <div
            key={top}
            className="absolute left-0 right-0 h-px pointer-events-none datastream-line"
            style={{
              top: `${top}%`,
              background:
                "linear-gradient(90deg, transparent 0%, #00e0ff 50%, transparent 100%)",
              boxShadow: "0 0 10px #00e0ff",
              animationDelay: `${i * 1.5}s`,
            }}
            aria-hidden="true"
          />
        ))}

        {/* Pupil dot — tracks cursor + heartbeat */}
        <div
          className="absolute top-1/2 left-1/2 w-3 h-3 rounded-full bg-black pupil-heartbeat"
          style={{
            transform:
              "translate(-50%, -50%) translate(var(--gaze-x), var(--gaze-y))",
            transition: "transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
            boxShadow:
              "0 0 8px rgba(0,0,0,0.9), 0 0 14px rgba(255,26,58,0.7), inset 0 0 4px rgba(255,80,120,0.5)",
          }}
          aria-hidden="true"
        />

        {/* Specular white shine (pupil sheen) */}
        <div
          className="absolute top-[44%] left-[48%] w-2 h-1 rounded-full bg-white specular pointer-events-none"
          style={{ opacity: 0.7, filter: "blur(1px)" }}
          aria-hidden="true"
        />
      </div>

      {/* Outer cyan halo glow (no longer a hard ring — soft radial) */}
      <div
        className="absolute inset-0 rounded-full pointer-events-none"
        style={{
          boxShadow: "0 0 60px rgba(0,224,255,0.18)",
          opacity: 0.6,
        }}
        aria-hidden="true"
      />
    </div>
  );
}
